<template>
  <div class="card">
    <PageHeader
        :showBtn="false"
        title="Message Log"
        subtitle="SMS and email messages sent by the system, one row per record, event and channel."
    />

    <div class="px-2 mt-2">
      <DateQuerySetter btnTitle="Go" @onClickGo="onClickGo">
        <div class="col-12 col-sm-6 col-md-3 col-lg-2">
          <label class="col-form-label">Event</label>
          <v-select
              v-model="filters.event"
              placeholder="select Event"
              :options="eventOptions"
              label="name"
              :reduce="option => option.id"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-3 col-lg-2">
          <label class="col-form-label">Status</label>
          <v-select
              v-model="filters.status"
              placeholder="select Status"
              :options="statusOptions"
              label="name"
              :reduce="option => option.id"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-3 col-lg-2">
          <label class="col-form-label">Channel</label>
          <v-select
              v-model="filters.channel"
              placeholder="Select Channel"
              :options="channelOptions"
              label="name"
              :reduce="option => option.id"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-3 col-lg-2">
          <label class="col-form-label">Search</label>
          <input
              v-model="filters.search"
              type="text"
              class="form-control"
              placeholder="Recipient, Reference or Party"
              @keyup.enter="onClickGo"
          >
        </div>
      </DateQuerySetter>
    </div>

    <div class="px-2 mt-2">
      <div class="table-responsive">
        <table class="table table-bordered table-hover">
          <thead>
          <tr>
            <th>SL</th>
            <th>Date</th>
            <th>Event</th>
            <th>Reference</th>
            <th>Party</th>
            <th>Channel</th>
            <th>Recipient</th>
            <th>Status</th>
            <th class="text-center">Attempts</th>
            <th>Sent At</th>
            <th>Details</th>
            <th>Error</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in tableItems" :key="item.id">
            <td>{{ getSerial(index) }}</td>
            <td class="text-nowrap">{{ formatDateTime(item.created_at) }}</td>
            <td>{{ getLabel(homeData.message_log_events, item.event) }}</td>
            <td>{{ item.reference_no }}</td>
            <td>{{ item.party_name }}</td>
            <td>{{ getLabel(homeData.message_log_channels, item.channel) }}</td>
            <td class="text-break">{{ item.recipient }}</td>
            <td>
              <span class="badge" :class="getStatusClass(item.status)">
                {{ getLabel(homeData.message_log_statuses, item.status) }}
              </span>
            </td>
            <td class="text-center">{{ item.attempts }}</td>
            <td class="text-nowrap">{{ item.sent_at_formatted }}</td>
            <td class="text-nowrap">{{ getDetails(item) }}</td>
            <td class="text-break">{{ item.error }}</td>
          </tr>
          </tbody>
        </table>
        <p v-if="! hasItems" class="text-center mt-5">{{ emptyText }}</p>
      </div>
    </div>

    <div class="px-2 mb-2">
      <Pagination :offset="offset" @onPageChange="onPageChange"/>
    </div>

    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import PageHeader from '@/components/atom/PageHeader.vue'
import DateQuerySetter from '@/components/atom/DateQuerySetter.vue'
import Pagination from '@/components/atom/Pagination.vue'
import Loader from '@/components/atom/LoaderComponent.vue'
import handleCompany from '@/services/modules/company'
import handleDropdowns from '@/services/modules/dropdowns'
import { resetPaginationPage } from '@/services/utils/global.js'

const STATUS_CLASSES = {
  pending: 'bg-secondary',
  processing: 'bg-info',
  sent: 'bg-success',
  failed: 'bg-danger',
  skipped: 'bg-warning'
}

const $route = useRoute()
const $router = useRouter()
const $store = useStore()
const showError = inject('showError')

const { fetchMessageLogs } = handleCompany()
const { fetchDropdownList } = handleDropdowns()

const loading = ref(false)
const hasLoaded = ref(false)
const offset = ref(20)
const tableItems = ref([])
const pageMeta = ref({ current_page: 1, per_page: 20 })
const homeData = ref({})
const filters = ref({ event: null, status: null, channel: null, search: '' })

const hasItems = computed(() => tableItems.value.length > 0)

const emptyText = computed(() => {
  if (! hasLoaded.value) return 'Select a date range and click Go'
  return 'No data found'
})

const toOptions = (source) => {
  if (! source) return []
  return Object.keys(source).map(id => ({ id, name: source[id] }))
}

const eventOptions = computed(() => toOptions(homeData.value.message_log_events))

const statusOptions = computed(() => toOptions(homeData.value.message_log_statuses))

const channelOptions = computed(() => toOptions(homeData.value.message_log_channels))

const getLabel = (source, key) => {
  if (! source) return key
  return source[key] || key
}

const getStatusClass = (status) => {
  return STATUS_CLASSES[status] || 'bg-secondary'
}

const formatDate = (value) => {
  if (! value) return ''
  return value.split('-').reverse().join('-')
}

const getReminderDayLabel = (days) => {
  const value = Number(days)
  if (value === 0) return 'On due date'
  if (value > 0) return value + ' day(s) before'
  return Math.abs(value) + ' day(s) after'
}

const DETAIL_BUILDERS = {
  invoice_due_reminder: (meta) => getReminderDayLabel(meta.reminder_days) + ' · due ' + formatDate(meta.due_date),
  invoice_due_manual: (meta) => meta.due_date ? 'Manual · due ' + formatDate(meta.due_date) : 'Manual'
}

const getDetails = (item) => {
  const builder = DETAIL_BUILDERS[item.event]
  if (! builder || ! item.meta_data) return ''
  return builder(item.meta_data)
}

const formatDateTime = (value) => {
  if (! value) return ''
  return new Date(value).toLocaleString('en-GB')
}

const getSerial = (index) => {
  return (pageMeta.value.current_page - 1) * pageMeta.value.per_page + index + 1
}

const hasDateRange = () => {
  return !! ($route.query.start && $route.query.end)
}

const buildQuery = (page) => {
  const params = new URLSearchParams({
    company_id: $route.params.companyId,
    start_date: $route.query.start,
    end_date: $route.query.end,
    offset: offset.value,
    page
  })
  if (filters.value.event) params.append('event', filters.value.event)
  if (filters.value.status) params.append('status', filters.value.status)
  if (filters.value.channel) params.append('channel', filters.value.channel)
  if (filters.value.search) params.append('search', filters.value.search)
  return '?' + params.toString()
}

const resetTable = () => {
  tableItems.value = []
  $store.commit('resetPagination')
}

const setLogs = (data) => {
  tableItems.value = data.data
  pageMeta.value = { current_page: data.current_page, per_page: data.per_page }
  $store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page
  })
}

const loadData = async (page) => {
  if (! hasDateRange()) {
    showError('Please select the From and To date')
    return
  }
  loading.value = true
  try {
    const res = await fetchMessageLogs(buildQuery(page))
    hasLoaded.value = true
    if (! res.status) {
      showError(res.message)
      resetTable()
      return
    }
    setLogs(res.data)
  } catch (err) {
    showError('Something is wrong. Check your connectivity!!')
  } finally {
    loading.value = false
  }
}

const onClickGo = async () => {
  resetPaginationPage($store, $route, $router)
  await loadData(1)
}

const onPageChange = async (page) => {
  const routeQuery = Object.assign({}, $route.query)
  routeQuery.page = page
  await $router.push({ path: $route.path, query: routeQuery })
  await loadData(page)
}

const getHomeData = async () => {
  try {
    const res = await fetchDropdownList(`?company_id=${$route.params.companyId}&is_home=1`)
    if (! res.status || ! res.data) return
    homeData.value = res.data.home_data || {}
  } catch (err) {
    showError('Something went wrong')
  }
}

const getRoutePage = () => {
  return Number($route.query.page) || 1
}

onMounted(() => {
  $store.commit('resetPagination')
  getHomeData()
  if (! hasDateRange()) return
  loadData(getRoutePage())
})
</script>
